import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge } from '@/components/ui';
import { useCosts } from './hooks/useCosts';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { CostTable } from './components/CostTable';
import { CostFormModal } from './components/CostFormModal';
import type { MaintenanceCost, CostType, CostStatus } from './types/maintenance-costs.types';

const typeLabels: Record<CostType, string> = {
  labor: 'Nhân công',
  material: 'Vật tư',
  equipment: 'Thiết bị',
  other: 'Khác',
};

const statusLabels: Record<CostStatus, string> = {
  pending: 'Chờ xử lý',
  verified: 'Đã xác minh',
  approved: 'Đã phê duyệt',
  rejected: 'Từ chối',
};

export default function CostListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();
  
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<MaintenanceCost | null>(null);

  const {
    costs,
    total,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useCosts(companyId);

  const formatCurrency = (val: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val);

  return (
    <PageLayout
      title="Theo dõi Chi phí Bảo dưỡng"
      description="Quản lý định mức tài chính cho nhân công, vật tư và thiết bị. Phê duyệt các khoản chi thực tế từ hiện trường."
      breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'Chi phí' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-chart-line">Báo cáo tài chính</Button>
          <AuthButton 
            module="maintenance" 
            action="create" 
            variant="primary" 
            icon="ri-add-line" 
            onClick={() => setShowCreate(true)}
          >
            Ghi nhận chi phí
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng chi tháng này"
            value={formatCurrency(stats?.totalCostThisMonth || 0)}
            icon="ri-money-dollar-circle-line"
            variant="primary"
          />
          <KPICard
            title="Chi phí Nhân công"
            value={formatCurrency(stats?.laborCost || 0)}
            icon="ri-group-line"
            variant="info"
          />
          <KPICard
            title="Chi phí Vật tư"
            value={formatCurrency(stats?.materialCost || 0)}
            icon="ri-seedling-line"
            variant="success"
          />
          <KPICard
            title="Sử dụng ngân sách"
            value={Math.round(stats?.budgetUtilization || 0)}
            icon="ri-pie-chart-line"
            variant="warning"
            suffix="%"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mô tả, mã tham chiếu..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'type', label: 'Loại chi phí', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      ...Object.entries(typeLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <CostTable 
            data={costs} 
            isLoading={isLoading} 
            onEdit={(item) => setEditItem(item)}
            onRowClick={(item) => navigate(`./detail/${item.id}`)} 
          />
        </Card>
      </div>

      <CostFormModal
        isOpen={showCreate || !!editItem}
        item={editItem}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditItem(null);
        }}
      />
    </PageLayout>
  );
}
